import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { paginationShape, searchQueryShape } from '@bibliocommons/bc-prop-types';
import { withRouter } from '@bibliocommons/utils-routing';
import ProgressBar from '@bibliocommons/shared-progress-bar';

import { VIEW, SEARCH_TYPES } from 'app/constants/SearchConstants';
import { METADATA_CATEGORY_TYPE, SEARCH_ORIGIN, UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import displayBibShape from 'app/shapes/displayBib';
import { trackContentClick, trackEventPdfCreate } from 'app/actions/AnalyticsGa4Actions';
import DrawerActions from 'app/actions/DrawerActions';
import SearchActions from 'app/actions/SearchActions';
import { eventShape } from 'app/shapes/events';
import {
  selectDisplayBibs,
  selectSearchResults,
  selectCalloutFeaturedEvent,
  selectSearchFacets,
  selectSearchQuery,
  selectActiveFilters,
  selectPagination,
  selectEventsRSSLink
} from 'app/selectors/events/EventsSearchSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import { activeFiltersShape, facetFieldsShape } from 'app/shapes/facets';
import EventsSearchPage from './EventsSearchPage';

const BIB_ROW_ITEM_LIMIT = 8;

export function EventsSearchPageContainer(props) {
  const { searchQuery, pagination, searchActions } = props;

  const intl = useIntl();
  const location = useLocation();
  const backToLinkHref = location.search ? `${location.pathname}${location.search}` : `${location.pathname}`;
  const resultsCount = pagination.get('count');
  const searchTerm = searchQuery.get('q');

  useEffect(() => {
    if (resultsCount === 0 && searchTerm) {
      searchActions.performCatalogSearch({
        limit: BIB_ROW_ITEM_LIMIT,
        query: searchTerm,
        searchType: SEARCH_TYPES.SMART,
        view: VIEW.GROUPED
      });
    }
  }, [resultsCount, searchTerm, searchQuery, searchActions]);

  const handlePdfCreateClick = eventCount => {
    const { trackPdfCreateClick } = props;

    trackPdfCreateClick({
      ui: {
        ui_content_total_count: eventCount
      }
    });
  };

  const trackCatologTitleClick = ({ bib }) => {
    const { metadataId, title } = bib;
    const { trackContentClick: dispatchTrackContentClick } = props;

    dispatchTrackContentClick({
      bibId: metadataId,
      search: {
        search_origin: SEARCH_ORIGIN.USER_QUERY,
        search_type: SEARCH_TYPES.KEYWORD,
        search_query: searchTerm
      },
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
        ui_content_layout: VIEW.MEDIUM,
        ui_content_page: pagination?.get('page'),
        ui_content_total_count: pagination?.get('count')
      },
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_value: title
      }
    });
  };

  return (
    <div>
      <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'events_search_page_title' })} />
      <ProgressBar active={props.isFetching} />
      <BackToLinkManager href={backToLinkHref} pageId="events" />
      <EventsSearchPage
        {...props}
        currentPageHref={backToLinkHref}
        handlePdfCreateClick={handlePdfCreateClick}
        trackCatologTitleClick={trackCatologTitleClick}
      />
    </div>
  );
}

EventsSearchPageContainer.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  drawer: ImmutablePropTypes.mapContains({
    open: PropTypes.bool
  }).isRequired,
  displayBibs: PropTypes.arrayOf(displayBibShape),
  events: ImmutablePropTypes.list.isRequired,
  calloutFeaturedEvent: eventShape,
  searchActions: PropTypes.shape({
    performCatalogSearch: PropTypes.func.isRequired
  }),
  searchQuery: searchQueryShape.isRequired,
  pagination: paginationShape.isRequired,
  facetFields: facetFieldsShape.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  singleBranch: PropTypes.bool,
  eventsRSSLink: PropTypes.string.isRequired,
  trackPdfCreateClick: PropTypes.func.isRequired,
  trackContentClick: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const library = selectCurrentLibrary(state);

  return {
    isFetching: state.getIn(['events', 'eventsSearch', 'isFetching'], false),
    drawer: state.get('drawer'),
    displayBibs: selectDisplayBibs(state),
    events: selectSearchResults(state),
    calloutFeaturedEvent: selectCalloutFeaturedEvent(state),
    facetFields: selectSearchFacets(state),
    searchQuery: selectSearchQuery(state),
    activeFilters: selectActiveFilters(state),
    pagination: selectPagination(state),
    singleBranch: library.get('singleBranch'),
    eventsRSSLink: selectEventsRSSLink(state),
    hasFeaturedEvents: state.getIn(['events', 'eventsSearch', 'hasFeaturedEvents'], false)
  };
};

const mapDispatchToProps = dispatch => ({
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  searchActions: bindActionCreators(SearchActions, dispatch),
  trackPdfCreateClick: bindActionCreators(trackEventPdfCreate, dispatch),
  trackContentClick: bindActionCreators(trackContentClick, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventsSearchPageContainer));
